import { useState, useMemo, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { FunnelIcon, CaretUpIcon, CaretDownIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { formatTimeInHMSString } from '../../../utils/formatTime';
import { QUESTION_DIFFICULTY } from '../../../utils/Question_Difficulty';
import SolutionDrawer from '../../../components/drawers/SolutionDrawer';

export default function AllQuestionsTable({ allQuestionsDetailedAnalysis }) {
  const [activeFilters, setActiveFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [openFilter, setOpenFilter] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [showQuestionDrawer, setShowQuestionDrawer] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionNumbers, setQuestionNumbers] = useState([]);

  useEffect(() => {
    if (allQuestionsDetailedAnalysis) {
      const uniqueSubjects = [...new Set(allQuestionsDetailedAnalysis.map((item) => item.subject))];
      setSubjects(uniqueSubjects);
      const uniqueChapters = [...new Set(allQuestionsDetailedAnalysis.map((item) => item.chapter))];
      setChapters(uniqueChapters);
      const uniqueTopics = [...new Set(allQuestionsDetailedAnalysis.map((item) => item.topics))];
      setTopics(uniqueTopics);

      const tempQuestionNumbers = allQuestionsDetailedAnalysis.map((item) => item.questionNumber);
      setQuestionNumbers(tempQuestionNumbers);
    }
  }, [allQuestionsDetailedAnalysis]);

  const renderDifficulty = (level) => {
    const difficultyMap = {
      1: { className: 'text-[var(--q3-success-normal)] text-sm' },
      2: { className: 'text-[var(--q3-warning-normal)] text-sm' },
      3: { className: 'text-[var(--q3-danger-normal)] text-sm' },
    };
    const difficulty = difficultyMap[level];
    return <p className={difficulty.className}>{QUESTION_DIFFICULTY[level]}</p>;
  };

  const getAttemptStatus = (status) => {
    const statusMap = {
      answered: { text: 'Answered' },
      answeredReview: { text: 'Answered & Marked for Review' },
      notAnswered: { text: 'Not Answered' },
      notVisited: { text: 'Not Visited' },
      markedReview: { text: 'Marked for Review' },
    };
    const statusInfo = statusMap[status];
    return <p className="text-sm text-[var(--q3-neutral-default)]">{statusInfo.text}</p>;
  };

  const renderStatusIcon = (isCorrect, item) => {
    const evalInfo =
      isCorrect === true
        ? { text: 'Correct', className: 'text-[var(--q3-success-normal)] text-sm' }
        : isCorrect === false && (item.status === 'answered' || item.status === 'answeredReview')
          ? { text: 'Wrong', className: 'text-[var(--q3-danger-normal)] text-sm' }
          : { text: 'Not Attempted', className: 'text-[var(--q3-neutral-light)] text-sm' };

    return (
      <div className="flex items-center justify-center w-full">
        {evalInfo.text === 'Correct' ? (
          <CheckIcon size={16} weight="bold" className={evalInfo.className} />
        ) : evalInfo.text === 'Wrong' ? (
          <XIcon size={16} weight="bold" className={evalInfo.className} />
        ) : (
          <p className={evalInfo.className}>-</p>
        )}
      </div>
    );
  };

  const renderOverview = (overview) => {
    const overviewMap = {
      perfect: { text: 'Perfect', className: 'text-sm text-[var(--q3-green-normal)]' },
      confused: { text: 'Confused', className: 'text-sm text-[var(--q3-neutral-light)]' },
      overtime: { text: 'Overtime', className: 'text-sm text-[var(--q3-orange-normal)]' },
      wasted: { text: 'Wasted', className: 'text-sm text-[var(--q3-danger-normal)]' },
    };
    const overviewInfo = overviewMap[overview];
    return <p className={overviewInfo?.className || 'text-sm'}>{overviewInfo?.text || '-'}</p>;
  };

  const columns = [
    {
      title: 'Qs No',
      key: 'questionNumber',
      align: 'center',
      sortable: true,
      render: (_, item) => (
        <div className="text-center h-7 w-7 p-[6px] ml-2 bg-[var(--q3-surface-dim)] rounded-full flex items-center justify-center gap-1">
          {item.questionNumber}
        </div>
      ),
    },
    {
      title: 'Subject',
      key: 'subject',
      align: 'left',
      filterable: true,
      filters: subjects.map((subj) => ({ text: subj, value: subj })),
      onFilter: (value, record) => record.subject === value,
    },
    {
      title: 'Chapter',
      key: 'chapter',
      align: 'left',
      filterable: true,
      filters: chapters.map((chapter) => ({ text: chapter, value: chapter })),
      onFilter: (value, record) => record.chapter === value,
    },
    {
      title: 'Topic',
      key: 'topics',
      align: 'left',
      filterable: true,
      filters: topics.map((topic) => ({ text: topic, value: topic })),
      onFilter: (value, record) => record.topics === value,
    },
    {
      title: 'Difficulty',
      key: 'level',
      align: 'center',
      render: renderDifficulty,
      filterable: true,
      filters: [
        { text: 'Easy', value: 1 },
        { text: 'Moderate', value: 2 },
        { text: 'Tough', value: 3 },
      ],
      onFilter: (value, record) => record.level === value,
    },
    {
      title: 'Time Spent',
      key: 'timeTaken',
      align: 'center',
      sortable: true,
      render: formatTimeInHMSString,
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      filterable: true,
      filters: [
        { text: 'Answered', value: 'answered' },
        { text: 'Answered & Marked for Review', value: 'answeredReview' },
        { text: 'Not Answered', value: 'notAnswered' },
        { text: 'Not Visited', value: 'notVisited' },
        { text: 'Marked for Review', value: 'markedReview' },
      ],
      onFilter: (value, record) => record.status === value,
      render: getAttemptStatus,
    },
    {
      title: 'Evaluation',
      key: 'isCorrect',
      align: 'center',
      render: renderStatusIcon,
      filterable: true,
      filters: [
        { text: 'Correct', value: 'Correct' },
        { text: 'Wrong', value: 'Wrong' },
        { text: 'Not Attempted', value: 'NotAttempted' },
      ],
      onFilter: (value, item) => {
        if (value === 'Correct') return item.isCorrect;
        if (value === 'Wrong')
          return !item.isCorrect && (item.status === 'answered' || item.status === 'answeredReview');
        return !['answered', 'answeredReview'].includes(item.status);
      },
    },
    {
      title: 'Overview',
      key: 'attemptOverview',
      align: 'center',
      render: renderOverview,
      filterable: true,
      filters: [
        { text: 'Perfect', value: 'perfect' },
        { text: 'Confused', value: 'confused' },
        { text: 'Overtime', value: 'overtime' },
        { text: 'Wasted', value: 'wasted' },
      ],
      onFilter: (value, item) => item.attemptOverview === value,
    },
  ];

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? undefined : value,
    }));
    setOpenFilter(null);
  };

  const handleQuestionClick = (item) => {
    setShowQuestionDrawer(true);
    setSelectedQuestion(item?.questionNumber);
  };

  const processedData = useMemo(() => {
    let filtered = allQuestionsDetailedAnalysis || [];

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (!value) return;
      const column = columns.find((col) => col.key === key);
      if (column?.onFilter) {
        filtered = filtered.filter((item) => column.onFilter(value, item));
      }
    });

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return sortConfig.direction === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    return filtered;
  }, [allQuestionsDetailedAnalysis, activeFilters, sortConfig]);

  const renderColumnHeader = (column) => {
    const hasActiveFilter = activeFilters[column.key];

    return (
      <div
        className="flex cursor-pointer items-center space-x-2"
        onClick={() => {
          if (column.sortable) {
            handleSort(column.key);
          } else if (column.filterable) {
            setOpenFilter(openFilter === column.key ? null : column.key);
          }
        }}
      >
        {column.sortable ? (
          <div className="flex items-center space-x-1">
            <span className="font-medium">{column.title}</span>
            <div className="flex flex-col">
              <CaretUpIcon
                size={12}
                className={`${sortConfig.key === column.key && sortConfig.direction === 'asc' ? 'text-[var(--q3-accent-normal)]' : 'text-[var(--q3-neutral-light)]'}`}
              />
              <CaretDownIcon
                size={12}
                className={`-mt-1 ${sortConfig.key === column.key && sortConfig.direction === 'desc' ? 'text-[var(--q3-accent-normal)]' : 'text-[var(--q3-neutral-light)]'}`}
              />
            </div>
          </div>
        ) : (
          <div className="font-medium">{column.title}</div>
        )}

        {column.filterable && (
          <Select
            open={openFilter === column.key}
            onOpenChange={(open) => setOpenFilter(open ? column.key : null)}
            value={activeFilters[column.key] ?? 'all'}
            onValueChange={(value) => handleFilterChange(column.key, value)}
          >
            <SelectTrigger
              className="w-4 h-4 p-0 !border-none !bg-transparent cursor-pointer shadow-none hover:bg-transparent [&>svg:last-child]:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <FunnelIcon
                size={12}
                className={`${hasActiveFilter ? 'text-[var(--q3-accent-normal)]' : 'text-[var(--q3-neutral-light)]'}`}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {column.filters.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto rounded-b-xl border border-t-0 border-[var(--q3-stroke-light)]">
        <table className="min-w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[var(--q3-surface-dim)]">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`whitespace-nowrap p-3 text-sm font-medium text-[var(--q3-neutral-default)] cursor-pointer ${
                    column.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                >
                  {renderColumnHeader(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-sm">
            {processedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-8">
                  No questions match the current filters.
                </td>
              </tr>
            ) : (
              processedData.map((item, index) => (
                <tr
                  key={item._id || index}
                  className={`cursor-pointer hover:bg-[var(--q3-surface-dim)] ${
                    processedData?.length === index + 1 ? '' : 'border-b border-[var(--q3-stroke-light)]'
                  }`}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`h-[104px] w-[154px] px-2 py-1 align-middle break-words whitespace-normal ${
                        column.align === 'center' ? 'text-center' : ''
                      }`}
                    >
                      {column.render ? column.render(item[column.key], item, index) : item[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showQuestionDrawer && selectedQuestion && (
        <SolutionDrawer
          qNo={selectedQuestion}
          questionNumbers={questionNumbers}
          isOpen={showQuestionDrawer}
          handleClose={() => {
            setSelectedQuestion(null);
            setShowQuestionDrawer(false);
          }}
        />
      )}
    </div>
  );
}
